import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import classNames from 'classnames';
import { Button } from 'components/Button/Button';
import type { GraphBreadcrumbType } from 'components/Graph/GraphBreadcrumbs/helpers/buildBreadcrumbs';
import { useRef } from 'react';
import type { FC, ReactNode } from 'react';
import { isAncestorElement } from 'utils/isAncestorElement';

type GraphBreadcrumbContainerStyle = 'primary' | 'default' | 'extra';

interface Props {
  children: ReactNode;
  className?: string;
  containerStyle: GraphBreadcrumbContainerStyle;
  /**
   * When set to true, the breadcrumb item should visually indicate that
   * it will be removed by clicking on whatever the mouse is currently on.
   */
  removalIndicated?: boolean;
  onClick?: () => void;
  onIndicateRemoveOthers?: (types: GraphBreadcrumbType[]) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  /**
   * Presence of this prop also tells whether to display the remove button or not.
   */
  onRemoveButtonClick?: () => void;
  /**
   * When mouse is hovered over the breadcrumb (anywhere except the remove button if present),
   * this will be passed back to 'onIndicateRemoveOthers' to tell some logic above
   * that such element types will be removed on click.
   */
  removedByBreadcrumbClick?: GraphBreadcrumbType[];
  /**
   * When mouse is hovered over the remove button,
   * this will be passed back to 'onIndicateRemoveOthers' to tell some logic above
   * that such element types will be removed on click.
   */
  removedByRemoveButtonClick?: GraphBreadcrumbType[];
}

export const GraphBreadcrumbContainer: FC<Props> = (props) => {
  const removeButtonRef = useRef<HTMLButtonElement | null>(null);
  const isRemoveButtonElement = (element: Element) =>
    removeButtonRef.current &&
    isAncestorElement(element, removeButtonRef.current);

  const indicateRemove = (types: GraphBreadcrumbType[]) => {
    if (props.onIndicateRemoveOthers) {
      props.onIndicateRemoveOthers(types);
    } else if (types.length > 0) {
      stumble(
        'You set a breadcrumb to indicate that click will remove other breadcrumbs,' +
          ' but did not provide onIndicateRemoveOthers callback',
      );
    }
  };

  return (
    <div
      className={classNames(
        'breadcrumbs__breadcrumb',
        'breadcrumbs__breadcrumb--clickable', // currently all graph breadcrumbs are clickable
        `breadcrumbs__breadcrumb--${props.containerStyle}`,
        'graph-breadcrumbs__breadcrumb',
        props.removalIndicated &&
          'breadcrumbs__breadcrumb--with-removal-indicator',
        props.onRemoveButtonClick &&
          'breadcrumbs__breadcrumb--with-remove-button',
        props.className,
      )}
      onClick={() => {
        props.onClick?.();
      }}
      onMouseEnter={() => {
        props.onMouseEnter?.();
      }}
      onMouseLeave={() => {
        indicateRemove([]);
        props.onMouseLeave?.();
      }}
      onMouseOver={(e) => {
        // It is possible that there will be some unnecessary calls to this handler when mouse traverses
        // between elements inside the breadcrumb. I decided for a simpler implementation over saving
        // those calls, because it is not an easy task to detect when mouse enters the remove button
        // and the rest of the breadcrumb element.
        if (isRemoveButtonElement(e.target as Element)) {
          indicateRemove(props.removedByRemoveButtonClick ?? []);
        } else {
          indicateRemove(props.removedByBreadcrumbClick ?? []);
        }
      }}
    >
      {props.removalIndicated && (
        <Button
          className='breadcrumbs__breadcrumb-remove breadcrumbs__breadcrumb-remove--indicator'
          variant='tertiary'
          size='xxxs'
          icon='fa-xmark'
          type='button'
        />
      )}
      {props.onRemoveButtonClick && (
        <Button
          className='breadcrumbs__breadcrumb-remove breadcrumbs__breadcrumb-remove--button'
          variant='tertiary'
          size='xxxs'
          icon='fa-xmark'
          onClick={(e) => {
            e.stopPropagation(); // so that it won't trigger click on the breadcrumb itself
            props.onRemoveButtonClick?.();
          }}
          ref={removeButtonRef}
          type='button'
        />
      )}
      {props.children}
    </div>
  );
};
